/**
 * Signer — unified interface for NIP-07 extensions and nsec-based signing.
 *
 * All code that needs to sign events or encrypt/decrypt should use this
 * interface rather than accessing window.nostr directly.
 */

import { finalizeEvent, getPublicKey } from 'nostr-tools/pure';
import * as nip44 from 'nostr-tools/nip44';
import { nip19 } from 'nostr-tools';
import type { UnsignedEvent, NostrEvent } from 'nostr-tools';

// ---------------------------------------------------------------------------
// Interface
// ---------------------------------------------------------------------------

export interface Signer {
  getPublicKey(): Promise<string>;
  signEvent(event: UnsignedEvent): Promise<NostrEvent>;
  nip44: {
    encrypt(pubkey: string, plaintext: string): Promise<string>;
    decrypt(pubkey: string, ciphertext: string): Promise<string>;
  };
}

// ---------------------------------------------------------------------------
// NIP-07 browser extension signer
// ---------------------------------------------------------------------------

interface Nip07Extension {
  getPublicKey(): Promise<string>;
  signEvent(event: UnsignedEvent): Promise<NostrEvent>;
  nip44?: {
    encrypt(pubkey: string, plaintext: string): Promise<string>;
    decrypt(pubkey: string, ciphertext: string): Promise<string>;
  };
}

function getNip07(): Nip07Extension | undefined {
  return (window as unknown as { nostr?: Nip07Extension }).nostr;
}

export class Nip07Signer implements Signer {
  async getPublicKey(): Promise<string> {
    const ext = getNip07();
    if (!ext) throw new Error('NIP-07 extension not found');
    return ext.getPublicKey();
  }

  async signEvent(event: UnsignedEvent): Promise<NostrEvent> {
    const ext = getNip07();
    if (!ext) throw new Error('NIP-07 extension not found');
    return ext.signEvent(event);
  }

  nip44 = {
    async encrypt(pubkey: string, plaintext: string): Promise<string> {
      const ext = getNip07();
      if (!ext) throw new Error('NIP-07 extension not found');
      if (!ext.nip44) throw new Error('NIP-44 not supported by your signer extension');
      return ext.nip44.encrypt(pubkey, plaintext);
    },
    async decrypt(pubkey: string, ciphertext: string): Promise<string> {
      const ext = getNip07();
      if (!ext) throw new Error('NIP-07 extension not found');
      if (!ext.nip44) throw new Error('NIP-44 not supported by your signer extension');
      return ext.nip44.decrypt(pubkey, ciphertext);
    },
  };
}

// ---------------------------------------------------------------------------
// nsec signer — signs locally using nostr-tools
// ---------------------------------------------------------------------------

export class NSecSigner implements Signer {
  private secretKey: Uint8Array;
  private pubkey: string;

  constructor(secretKey: Uint8Array) {
    this.secretKey = secretKey;
    this.pubkey = getPublicKey(secretKey);
  }

  /** Create from an nsec bech32 string. */
  static fromNsec(nsec: string): NSecSigner {
    const decoded = nip19.decode(nsec);
    if (decoded.type !== 'nsec') throw new Error('Invalid nsec');
    return new NSecSigner(decoded.data);
  }

  async getPublicKey(): Promise<string> {
    return this.pubkey;
  }

  async signEvent(event: UnsignedEvent): Promise<NostrEvent> {
    return finalizeEvent(event, this.secretKey) as unknown as NostrEvent;
  }

  nip44 = {
    encrypt: async (pubkey: string, plaintext: string): Promise<string> => {
      const conversationKey = nip44.getConversationKey(this.secretKey, pubkey);
      return nip44.encrypt(plaintext, conversationKey);
    },
    decrypt: async (pubkey: string, ciphertext: string): Promise<string> => {
      const conversationKey = nip44.getConversationKey(this.secretKey, pubkey);
      return nip44.decrypt(ciphertext, conversationKey);
    },
  };
}

// ---------------------------------------------------------------------------
// Storage key for the active signer type
// ---------------------------------------------------------------------------

const SIGNER_TYPE_KEY = 'tile-studio:signer-type';
const NSEC_KEY = 'tile-studio:nsec';

export type SignerType = 'nip07' | 'nsec';

function getStoredSignerType(): SignerType | null {
  return localStorage.getItem(SIGNER_TYPE_KEY) as SignerType | null;
}

export function storeSignerType(type: SignerType): void {
  localStorage.setItem(SIGNER_TYPE_KEY, type);
}

export function storeNsec(nsec: string): void {
  localStorage.setItem(NSEC_KEY, nsec);
}

function getStoredNsec(): string | null {
  return localStorage.getItem(NSEC_KEY);
}

export function clearSignerStorage(): void {
  localStorage.removeItem(SIGNER_TYPE_KEY);
  localStorage.removeItem(NSEC_KEY);
}

/**
 * Create a signer from stored credentials.
 * Returns null if no credentials are stored or if the extension is unavailable.
 */
export function createStoredSigner(): Signer | null {
  const type = getStoredSignerType();
  if (type === 'nsec') {
    const nsec = getStoredNsec();
    if (nsec) {
      try {
        return NSecSigner.fromNsec(nsec);
      } catch {
        return null;
      }
    }
  }
  if (type === 'nip07') {
    return new Nip07Signer();
  }
  // Fallback: if a pubkey exists but no signer type is stored (pre-refactor login),
  // assume NIP-07 extension was used.
  if (!type && localStorage.getItem('tile-studio:pubkey')) {
    return new Nip07Signer();
  }
  return null;
}

/** Check if NIP-07 extension is available. */
export function isNip07Available(): boolean {
  return !!getNip07();
}
